import process from 'node:process';
import { getArgValue, hasFlag } from './auth.js';
import { apiRequest } from './cli-helpers.js';

type FailureLogsUrlResponse = {
  ok: boolean;
  error?: string;
  data?: { url: string; key: string };
};

type LiveLogsResponse = {
  ok: boolean;
  error?: string;
  data: { logs: string };
};

export type FailureLogsBundle = { text: string; key: string } | { error: string };

type FailureLogsDependencies = {
  requestFailureLogsUrl?: (
    apiUrl: string,
    token: string,
    instanceId: string
  ) => Promise<FailureLogsUrlResponse>;
  fetch?: (url: string) => Promise<Response>;
};

const requestFailureLogsUrl = (apiUrl: string, token: string, instanceId: string) =>
  apiRequest<FailureLogsUrlResponse>(
    apiUrl,
    token,
    'GET',
    `/org/environments/instances/${instanceId}/logs/failure`
  );

export async function decodeFailureLogsBundle(buf: Buffer): Promise<string> {
  const { gunzipSync } = await import('node:zlib');
  try {
    return gunzipSync(buf).toString('utf8');
  } catch {
    return buf.toString('utf8');
  }
}

/**
 * Fetch a failed/terminated instance's captured full-log bundle: presign a GET
 * off the API, download the gzip from S3, and return the decompressed text.
 * Returns `{ error }` (never throws) so callers can fall back cleanly.
 */
export async function fetchFailureLogsBundle(
  apiUrl: string,
  token: string,
  instanceId: string,
  deps: FailureLogsDependencies = {}
): Promise<FailureLogsBundle> {
  const res = await (deps.requestFailureLogsUrl ?? requestFailureLogsUrl)(apiUrl, token, instanceId);
  if (!res.ok || !res.data?.url) {
    return { error: res.error ?? 'No captured diagnostic logs' };
  }
  try {
    const resp = await (deps.fetch ?? fetch)(res.data.url);
    if (!resp.ok) return { error: `Failed to download bundle (HTTP ${resp.status})` };
    const buf = Buffer.from(await resp.arrayBuffer());
    return { text: await decodeFailureLogsBundle(buf), key: res.data.key };
  } catch (err) {
    return { error: err instanceof Error ? err.message : String(err) };
  }
}

export async function handleLogs(argv: string[], apiUrl: string, token: string): Promise<void> {
  const positional = argv.filter((a) => !a.startsWith('-'));
  const instanceId = positional[0];
  const json = hasFlag(argv, '--json');

  if (!instanceId) {
    console.error('Error: instance ID required. Usage: canary sandbox logs <instance-id>');
    process.exit(1);
  }

  const service = getArgValue(argv, '--service');
  const tail = getArgValue(argv, '--tail') ?? '200';
  const source = getArgValue(argv, '--source');
  const forceFailure = hasFlag(argv, '--failure') || source === 'failure';

  // Failure snapshot: the durable full-log bundle captured to S3 when the
  // instance failed to provision. Works for `failed`/`terminated` instances
  // (live docker/logs needs a running host). Forced with --failure/--source
  // failure, and used as an automatic fallback when the live tail can't run.
  if (forceFailure) {
    const bundle = await fetchFailureLogsBundle(apiUrl, token, instanceId);
    if ('error' in bundle) {
      console.error(`Error: ${bundle.error}`);
      process.exit(1);
    }
    if (json) {
      console.log(JSON.stringify(bundle, null, 2));
      return;
    }
    process.stdout.write(bundle.text || '(empty bundle)');
    return;
  }

  // Live path: tail the running containers via SSM. Don't send `service: ''`
  // — the server's regex validator rejects empty strings even though the field
  // is optional, and the route then returns 500 instead of an all-services tail.
  const res = await apiRequest<LiveLogsResponse>(
    apiUrl,
    token,
    'POST',
    `/org/environments/instances/${instanceId}/docker/logs`,
    {
      ...(service ? { service } : {}),
      tail: parseInt(tail, 10),
    }
  );

  if (!res.ok) {
    // The instance likely isn't running (failed/terminated). Fall back to the
    // captured failure snapshot so a dead provision is still diagnosable.
    const bundle = await fetchFailureLogsBundle(apiUrl, token, instanceId);
    if (!('error' in bundle)) {
      if (json) {
        console.log(JSON.stringify(bundle, null, 2));
        return;
      }
      console.error(`(live logs unavailable: ${res.error}; showing captured failure snapshot)\n`);
      process.stdout.write(bundle.text || '(empty bundle)');
      return;
    }
    console.error(`Error: ${res.error}`);
    process.exit(1);
  }

  if (json) {
    console.log(JSON.stringify(res.data, null, 2));
    return;
  }

  process.stdout.write(res.data.logs ?? '(no logs)');
}
